import * as React from 'react';
import type { MeasureProps } from 'react-measure';
import Measure from 'react-measure';

import classNames from 'classnames';

import { isRightToLeft } from 'js/lib/language';

import { color } from '@coursera/coursera-ui';
import { SvgChevronLeft, SvgChevronRight } from '@coursera/coursera-ui/svg';

import Markdown from 'bundles/cms/components/Markdown';
import type { FaqProps } from 'bundles/cms/types';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/page';

import 'css!bundles/design-system/components/__styles__/Faq';

type Props = {
  faq: FaqProps;
  index: number;
  isExpanded?: boolean;
  key: string;
  isBrowsePage?: boolean;
  component?: React.ElementType;
};

type State = {
  expanded: boolean;
  containerHeight: number;
};

class Faq extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { isExpanded } = this.props;

    this.state = {
      expanded: isExpanded || false,
      containerHeight: 84,
    };
  }

  onResize: MeasureProps['onResize'] = (contentRect) => {
    this.setState(() => ({
      // We pass "bounds" into <Measure /> to request this in render() below.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      containerHeight: contentRect.bounds!.height,
    }));
  };

  toggleFaq = () => {
    this.setState((state) => ({ expanded: !state.expanded }));
  };

  render() {
    const { faq, index, isBrowsePage, component } = this.props;
    const { expanded, containerHeight } = this.state;
    const { question, answer } = faq;
    const trackingName = isBrowsePage ? 'browse_toggle_faq_' : 'toggle_faq_';
    const WrapperComponent = component ?? 'li';

    return (
      <WrapperComponent className={classNames('rc-Faq', { expanded })}>
        <TrackedButton
          id={`faq-${index}`}
          className="question"
          aria-expanded={expanded}
          aria-controls={`faq-detail-${index}`}
          onClick={this.toggleFaq}
          trackingName={trackingName + index}
        >
          {isRightToLeft(_t.getLocale()) ? (
            <SvgChevronLeft
              style={{ transform: expanded ? 'rotate(-90deg)' : 'rotate(0deg)' }}
              color={color.primary}
              aria-hidden={true}
            />
          ) : (
            <SvgChevronRight
              style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
              color={color.primary}
              aria-hidden={true}
            />
          )}

          <h3 className={classNames('p-l-1s', { 'browse-faq-question': isBrowsePage })}>{question}&lrm;</h3>
        </TrackedButton>

        <div
          id={`faq-detail-${index}`}
          className={classNames('transitionContainer', { 'p-a-2': expanded })}
          aria-labelledby={`faq-${index}`}
          role="region"
          aria-hidden={!expanded}
          style={{
            height: `${containerHeight}px`,
            overflow: 'hidden',
            transition: 'height 0.3s cubic-bezier(0.23, 1, 0.32, 1)',
          }}
        >
          <Measure bounds onResize={this.onResize}>
            {({ measureRef }) => (
              <div ref={measureRef}>
                <Markdown
                  className={classNames('answer', { visible: expanded, 'browse-faq-answer': isBrowsePage })}
                  source={`${answer}&lrm;`}
                />
              </div>
            )}
          </Measure>
        </div>
      </WrapperComponent>
    );
  }
}

export default Faq;
